/**
 * Premium purchase blocks.
 *
 * <premium-offers> takes over the submit of the theme's <product-form-component>
 * (main button and the sticky bar, which clicks it) and Buy it now, and adds the
 * selected offer itself in one /cart/add.js call. It does not rely on the native
 * quantity input, which the theme resets to 1 when it re-renders after a variant change.
 * The native quantity is still kept in sync for the sticky bar's display.
 *
 * With "Color per item" on, the selected offer shows one dropdown per item and each
 * item is added in its own color. Item #1 drives the main variant picker.
 */
(() => {
  if (customElements.get('premium-offers')) return;

  const SEPARATORS = {
    amount: [2, ',', '.'],
    amount_no_decimals: [0, ',', '.'],
    amount_with_comma_separator: [2, '.', ','],
    amount_no_decimals_with_comma_separator: [0, '.', ','],
    amount_with_apostrophe_separator: [2, "'", '.'],
    amount_with_space_separator: [2, ' ', ','],
    amount_no_decimals_with_space_separator: [0, ' ', ','],
  };

  /**
   * shop.money_format can contain HTML added by currency apps (e.g. <span class=money>),
   * sometimes HTML-escaped. Prices here are written as text, so keep only the plain pattern.
   * @param {string} format
   */
  function cleanFormat(format) {
    const box = document.createElement('textarea');
    box.innerHTML = String(format || '');
    return box.value.replace(/<[^>]*>/g, '').trim();
  }

  /**
   * @param {number} cents
   * @param {string} format - cleaned shop.money_format
   */
  function formatMoney(cents, format) {
    const template = format || '${{amount}}';
    return template.replace(/\{\{\s*(\w+)\s*\}\}/, (_match, key) => {
      const [decimals, thousands, decimal] = SEPARATORS[key] || SEPARATORS.amount;
      const fixed = (Math.round(Number(cents) || 0) / 100).toFixed(decimals);
      const [whole, fraction] = fixed.split('.');
      const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return fraction ? `${grouped}${decimal}${fraction}` : grouped;
    });
  }

  /** @param {unknown} image - variant.featured_image from the variant JSON */
  function imageUrl(image) {
    const src = typeof image === 'string' ? image : image && typeof image === 'object' ? image.src : '';
    if (!src) return '';
    try {
      const url = new URL(src, window.location.href);
      url.searchParams.set('width', '160');
      return url.toString();
    } catch {
      return '';
    }
  }

  class PremiumOffers extends HTMLElement {
    connectedCallback() {
      this.section = this.closest('.shopify-section') || document;
      this.abort = new AbortController();
      const { signal } = this.abort;

      const formatNode = this.querySelector('[data-money-format]');
      let rawFormat = '';
      try {
        rawFormat = formatNode ? JSON.parse(formatNode.textContent || '""') : '';
      } catch {
        rawFormat = '';
      }
      this.moneyFormat = cleanFormat(rawFormat);

      this.price = Number(this.dataset.price) || 0;
      this.compare = Number(this.dataset.compare) || 0;
      this.notice = this.querySelector('[data-pp-notice]');

      /** @type {Array<{id: number, options: string[], available: boolean, price: number, compare: number, image: string}>} */
      this.variants = [];
      const variantsNode = this.querySelector('[data-pp-variants]');
      if (variantsNode) {
        try {
          this.variants = JSON.parse(variantsNode.textContent || '[]');
        } catch {
          this.variants = [];
        }
      }
      this.optionIndex = parseInt(this.dataset.itemOptionIndex ?? '', 10);
      this.hasPickers = this.variants.length > 0 && this.optionIndex >= 0;
      this.currentVariant = this.findVariantById(this.dataset.variantId);
      this.busy = false;
      /** @type {'missing' | 'soldout' | null} */
      this.mainState = null;

      this.addEventListener(
        'change',
        (event) => {
          const target = event.target;
          if (target instanceof HTMLInputElement && target.matches('.pp-offer__input')) {
            this.syncItems();
            this.apply();
          } else if (target instanceof HTMLSelectElement && target.matches('[data-pp-item-select]')) {
            this.onItemChange(target);
          }
        },
        { signal }
      );

      // The theme delegates submit/click from a capture listener on document, so listen on
      // window (captured first) and stop the event before the theme adds the item as well.
      window.addEventListener('submit', this.onSubmit, { capture: true, signal });
      window.addEventListener('click', this.onBuyNowClick, { capture: true, signal });
      if (this.hasPickers) this.syncItems();

      this.section.addEventListener('variant:update', this.onVariantUpdate, { signal });
      this.section.addEventListener(
        'change',
        (event) => {
          if (event.target instanceof Element && event.target.closest('variant-picker')) this.updateLegends();
        },
        { signal }
      );

      this.apply();
      this.updateLegends();
      customElements.whenDefined('quantity-selector-component').then(() => this.apply());

      // The theme re-renders the buy buttons after a variant change and resets the quantity
      // to 1; put the offer quantity back whenever that happens.
      let queued = false;
      this.observer = new MutationObserver(() => {
        if (queued) return;
        queued = true;
        requestAnimationFrame(() => {
          queued = false;
          const input = this.productForm?.querySelector('input[name="quantity"]');
          if (input instanceof HTMLInputElement && input.value !== String(this.quantity)) this.apply();
        });
      });
      this.observer.observe(this.section, { childList: true, subtree: true });
    }

    disconnectedCallback() {
      this.abort?.abort();
      this.observer?.disconnect();
    }

    get quantity() {
      const checked = this.querySelector('.pp-offer__input:checked');
      return Math.max(1, parseInt(checked?.dataset.qty || '1', 10) || 1);
    }

    get productForm() {
      return this.section.querySelector(`product-form-component[data-product-id="${this.dataset.productId}"]`);
    }

    /** Push the selected offer quantity into the native product form. */
    apply() {
      const quantity = this.quantity;
      const productForm = this.productForm;

      if (productForm) {
        const selector = productForm.querySelector('quantity-selector-component');
        const input = productForm.querySelector('input[name="quantity"]');

        if (selector && typeof selector.setValue === 'function') {
          selector.setValue(String(quantity));
          if (typeof selector.updateButtonStates === 'function') selector.updateButtonStates();
        } else if (input) {
          input.value = String(quantity);
        } else {
          const form = productForm.querySelector('form');
          if (form) {
            const hidden = document.createElement('input');
            hidden.type = 'hidden';
            hidden.name = 'quantity';
            hidden.value = String(quantity);
            form.appendChild(hidden);
          }
        }

        productForm.dataset.quantityDefault = String(quantity);

        // Same event the native quantity selector fires; the native sticky bar listens for it
        // and shows the quantity in its button, so only send it when that is wanted.
        if (this.dataset.showQty === 'true') {
          (selector || productForm).dispatchEvent(
            new CustomEvent('quantity-selector:update', { bubbles: true, detail: { quantity } })
          );
        }
      }

      this.updatePrices();
      this.updateButtonLabel();
    }

    updatePrices() {
      const showCompare = this.dataset.showCompare === 'true';
      this.querySelectorAll('.pp-offer__input').forEach((input) => {
        const card = input.closest('.pp-offer');
        if (!card) return;
        const qty = parseInt(input.dataset.qty || '1', 10) || 1;
        const discount = Math.min(90, Math.max(0, Number(input.dataset.discount) || 0));

        let total = this.price * qty;
        let compareTotal = this.compare > this.price ? this.compare * qty : 0;
        const items = this.hasPickers ? this.itemVariants(card) : [];
        if (items.length && items.every(Boolean)) {
          total = items.reduce((sum, v) => sum + v.price, 0);
          compareTotal = items.reduce((sum, v) => sum + Math.max(v.compare, v.price), 0);
          if (compareTotal <= total) compareTotal = 0;
        }
        const final = discount > 0 ? Math.round((total * (100 - discount)) / 100) : total;

        let strike = 0;
        if (discount > 0) strike = total;
        else if (compareTotal > total) strike = compareTotal;

        const priceNode = card.querySelector('[data-pp-price]');
        const compareNode = card.querySelector('[data-pp-compare]');
        if (priceNode) priceNode.textContent = formatMoney(final, this.moneyFormat);
        if (compareNode) compareNode.textContent = showCompare && strike > final ? formatMoney(strike, this.moneyFormat) : '';
      });
    }

    /** Swap the card thumbnails to the selected variant's image. */
    updateImages(variant) {
      const url = imageUrl(variant?.featured_image);
      this.querySelectorAll('.pp-offer').forEach((card) => {
        const items = this.hasPickers ? this.itemVariants(card) : [];
        card.querySelectorAll('[data-pp-img]').forEach((img, index) => {
          const src = items[index]?.image || url;
          if (src && img instanceof HTMLImageElement && img.src !== src) img.src = src;
        });
      });
    }

    /* ---------- Color per item ---------- */

    /** @param {string | number | undefined} id */
    findVariantById(id) {
      return this.variants.find((v) => String(v.id) === String(id)) || null;
    }

    /** Variant matching the main selection with the per-item option swapped to `value`. */
    findVariantWith(value) {
      const base = this.currentVariant?.options;
      if (!base) return null;
      return (
        this.variants.find((v) =>
          v.options.every((opt, i) => (i === this.optionIndex ? opt === value : opt === base[i]))
        ) || null
      );
    }

    /** @param {Element} card */
    itemSelects(card) {
      return /** @type {HTMLSelectElement[]} */ (Array.from(card.querySelectorAll('[data-pp-item-select]')));
    }

    /** @param {Element} card - one variant (or null when the combination doesn't exist) per item */
    itemVariants(card) {
      return this.itemSelects(card).map((select) => this.findVariantWith(select.value));
    }

    get selectedCard() {
      return this.querySelector('.pp-offer__input:checked')?.closest('.pp-offer') || null;
    }

    /** Selected card's items as cart lines, or null if any item can't be bought. */
    selectedLines() {
      const card = this.selectedCard;
      if (!card) return null;
      const items = this.itemVariants(card);
      if (!items.length || items.some((v) => !v || !v.available)) return null;
      /** @type {Map<number, number>} */
      const lines = new Map();
      items.forEach((v) => lines.set(v.id, (lines.get(v.id) || 0) + 1));
      return Array.from(lines, ([id, quantity]) => ({ id, quantity }));
    }

    /**
     * Cart lines for the selected offer: one per chosen color, or the form's variant × quantity.
     * @returns {Array<{id: number, quantity: number}> | null} null when nothing can be added
     */
    cartLines() {
      if (this.hasPickers) return this.selectedLines();
      const id = Number(this.productForm?.querySelector('input[name="id"]')?.value);
      return id ? [{ id, quantity: this.quantity }] : null;
    }

    /**
     * Keep item #1 (and items the customer hasn't touched) on the main selection,
     * refresh swatches, sold-out states and the notice.
     */
    syncItems() {
      if (!this.hasPickers) return;
      const mainValue = this.currentVariant?.options[this.optionIndex];

      this.querySelectorAll('.pp-offer').forEach((card) => {
        this.itemSelects(card).forEach((select, index) => {
          if (mainValue && (index === 0 || select.dataset.touched !== 'true')) select.value = mainValue;

          Array.from(select.options).forEach((option) => {
            const v = this.findVariantWith(option.value);
            option.disabled = !v || !v.available;
          });

          // A touched item that became unavailable (e.g. size changed) falls back to the main color.
          const current = this.findVariantWith(select.value);
          if ((!current || !current.available) && mainValue && index > 0) {
            const fallback = this.findVariantWith(mainValue);
            if (fallback?.available) select.value = mainValue;
          }

          this.paintItem(select);
        });
      });

      this.updateItemNotice();
    }

    /** @param {HTMLSelectElement} select */
    paintItem(select) {
      const option = select.selectedOptions[0];
      const swatch = select.parentElement?.querySelector('[data-pp-swatch]');
      if (swatch instanceof HTMLElement && option) {
        const image = option.dataset.swatchImage;
        swatch.style.setProperty('--pp-swatch', image ? `url("${image}")` : option.dataset.swatch || '#d1d5db');
      }
      const v = this.findVariantWith(select.value);
      select.closest('[data-pp-item]')?.classList.toggle('pp-item--unavailable', !v || !v.available);
    }

    updateItemNotice() {
      if (!this.hasPickers || !this.notice) return;
      const card = this.selectedCard;
      const blocked = card ? this.itemVariants(card).some((v) => !v || !v.available) : false;
      if (blocked && this.mainState !== 'missing') {
        this.notice.textContent = this.dataset.textItemUnavailable || this.dataset.textSoldout || '';
        this.notice.hidden = !this.notice.textContent;
      } else {
        this.setNotice(this.mainState);
      }
    }

    /** @param {HTMLSelectElement} select */
    onItemChange(select) {
      select.dataset.touched = 'true';
      this.paintItem(select);

      // Item #1 drives the main picker, so the gallery, price and stock follow it.
      if (select.dataset.index === '0') this.selectMainOption(select.value);

      this.updateItemNotice();
      this.updatePrices();
      this.updateImages(null);
    }

    /** Select `value` for the per-item option in the (possibly hidden) main variant picker. */
    selectMainOption(value) {
      const picker = this.section.querySelector(`variant-picker[data-product-id="${this.dataset.productId}"]`);
      if (!picker) return;
      const node = picker.querySelector('.variant-picker__form')?.children[this.optionIndex];
      if (!node) return;

      const radio = Array.from(node.querySelectorAll('input[type="radio"]')).find(
        (input) => input instanceof HTMLInputElement && input.value === value
      );
      if (radio instanceof HTMLInputElement) {
        if (!radio.checked) radio.click();
        return;
      }
      const select = node.querySelector('select');
      if (select instanceof HTMLSelectElement && select.value !== value) {
        select.value = value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    /** @param {Event} event */
    onSubmit = (event) => {
      const form = event.target;
      const productForm = this.productForm;
      if (!(form instanceof HTMLFormElement) || !productForm?.contains(form)) return;

      const lines = this.cartLines();
      if (!lines && !this.hasPickers) return; // no variant id to read: leave it to the theme

      event.preventDefault();
      event.stopImmediatePropagation();
      if (lines) this.addLines(lines, false);
      else this.updateItemNotice();
    };

    /** Buy it now only knows one variant and ignores the offer; add the offer and go to checkout instead. */
    onBuyNowClick = (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      const productForm = this.productForm;
      if (!productForm?.contains(target)) return;
      if (!target.closest('.shopify-payment-button, shopify-accelerated-checkout, shopify-buy-it-now-button')) return;
      const lines = this.cartLines();
      if (!lines && !this.hasPickers) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      if (lines) this.addLines(lines, true);
      else this.updateItemNotice();
    };

    /**
     * @param {Array<{id: number, quantity: number}>} lines
     * @param {boolean} toCheckout
     */
    async addLines(lines, toCheckout) {
      if (this.busy) return;

      const productForm = this.productForm;
      const button = productForm?.querySelector('[ref="addToCartButton"]');
      this.busy = true;
      if (button instanceof HTMLButtonElement) button.setAttribute('aria-busy', 'true');

      // Ask for the cart sections so the cart drawer updates like it does for the native add.
      const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
        .map((node) => (node instanceof HTMLElement ? node.dataset.sectionId : ''))
        .filter(Boolean);

      const root = window.Shopify?.routes?.root || '/';
      try {
        const response = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: lines,
            ...(sectionIds.length && !toCheckout
              ? { sections: sectionIds.join(','), sections_url: window.location.pathname }
              : {}),
          }),
        });
        const data = await response.json();

        if (!response.ok || data.status) {
          if (!this.notice) return;
          this.notice.textContent = data.description || data.message || this.dataset.textItemUnavailable || '';
          this.notice.hidden = !this.notice.textContent;
          return;
        }

        if (toCheckout) {
          window.location.href = `${root}checkout`;
          return;
        }

        let cart;
        try {
          cart = await (await fetch(`${root}cart.js`)).json();
        } catch {
          cart = undefined;
        }

        // Same event the native product form dispatches (header count, cart drawer).
        const itemCount = lines.reduce((sum, line) => sum + line.quantity, 0);
        const cartEvent = new Event('cart:update', { bubbles: true });
        /** @type {any} */ (cartEvent).detail = {
          resource: cart,
          sourceId: productForm?.id || '',
          data: { source: 'premium-offers', itemCount, productId: this.dataset.productId, sections: data.sections },
        };
        (productForm || this).dispatchEvent(cartEvent);
      } catch (error) {
        console.error(error);
      } finally {
        this.busy = false;
        if (button instanceof HTMLButtonElement) button.removeAttribute('aria-busy');
      }
    }

    updateButtonLabel() {
      const button = this.productForm?.querySelector('[ref="addToCartButton"]');
      if (!(button instanceof HTMLButtonElement) || button.disabled) return;

      const label = button.querySelector('.add-to-cart-text__content > span > span');
      if (!label) return;

      const quantity = this.quantity;
      const single = this.dataset.labelSingle || label.textContent || '';
      const multi = this.dataset.labelMulti || single;
      const useMulti = quantity > 1 && this.dataset.showQty === 'true';
      const text = (useMulti ? multi : single).replace(/\[qty\]/g, String(quantity));
      if (label.textContent !== text) label.textContent = text;
    }

    /** Show "Color: Onyx" style labels above button-style options. */
    updateLegends() {
      if (this.dataset.showSelected !== 'true') return;
      this.section.querySelectorAll('variant-picker fieldset.variant-option--buttons').forEach((fieldset) => {
        if (fieldset.classList.contains('variant-option--swatches')) return; // native swatches already show it
        const legend = fieldset.querySelector('legend');
        if (!legend) return;
        const checked = fieldset.querySelector('input:checked');
        let value = legend.querySelector('.pp-selected-value');
        if (!value) {
          value = document.createElement('span');
          value.className = 'pp-selected-value';
          legend.appendChild(value);
        }
        value.textContent = checked instanceof HTMLInputElement ? checked.value : '';
      });
    }

    /** @param {'missing' | 'soldout' | null} state */
    setNotice(state) {
      if (!this.notice) return;
      if (!state) {
        this.notice.hidden = true;
        this.notice.textContent = '';
        return;
      }
      this.notice.textContent = state === 'missing' ? this.dataset.textMissing || '' : this.dataset.textSoldout || '';
      this.notice.hidden = !this.notice.textContent;
    }

    /** @param {CustomEvent} event */
    onVariantUpdate = (event) => {
      const data = event.detail?.data;
      if (data?.productId && String(data.productId) !== String(this.dataset.productId)) return;

      const variant = event.detail?.resource || null;
      if (variant) {
        this.price = Number(variant.price) || 0;
        this.compare = Number(variant.compare_at_price) || 0;
        if (this.hasPickers) this.currentVariant = this.findVariantById(variant.id) || this.currentVariant;
      }

      this.mainState = !variant ? 'missing' : variant.available === false ? 'soldout' : null;
      this.setNotice(this.mainState);
      if (variant) {
        this.syncItems();
        this.updateImages(variant);
      }
      this.updatePrices();

      // The product form morphs its button after this event; re-apply once it has.
      setTimeout(() => {
        this.apply();
        this.updateLegends();
      }, 0);
    };
  }

  customElements.define('premium-offers', PremiumOffers);

  // Size guide: close when the backdrop is clicked.
  document.addEventListener('click', (event) => {
    const dialog = event.target;
    if (dialog instanceof HTMLDialogElement && dialog.classList.contains('pp-size-guide__dialog')) {
      const rect = dialog.getBoundingClientRect();
      const inside =
        event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom;
      if (!inside) dialog.close();
    }
  });
})();
